import { isAbortOrCanceledError } from "@mirage-web/utils/abort-signals";
import { z } from "zod";

import {
  ApiHost,
  ApiResponse,
  createClientTrace,
  makeApiRequest,
  type ReplayableRequestBody,
} from "./api-client";
import { NetworkAnalyticsDataRecord, NetworkingLayerConfig } from "./config";
import { ApiUnavailableError, KnownApiError } from "./known-api-error";
import { replacePathParams } from "./path-params";

export type QueryMethod = "POST" | "PUT" | "PATCH" | "GET" | "DELETE";

type ExtractParamFromPath<Path, NextPart> = Path extends `:${infer Param}.${string}`
  ? Record<Param, string> & NextPart
  : Path extends `:${infer Param}`
    ? Record<Param, string> & NextPart
    : NextPart;

export type ExtractParamsFromPath<Path> = Path extends `${infer Segment}/${infer Rest}`
  ? ExtractParamFromPath<Segment, ExtractParamsFromPath<Rest>>
  : ExtractParamFromPath<Path, {}>;

export type NetworkAnalyticsOptions<
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
> =
  // no events will be logged
  | { level: "none" }
  // events will be logged but no request params or response data
  | { level: "basic" }
  // events will be logged with request params and response data that is selected
  | {
      level: "custom";

      // defaults to true
      shouldLogRequest?: (
        status: "success" | "fail" | "cancel",
        params: z.infer<ParamsSchema>,
        response?: z.infer<ResponseSchema>
      ) => boolean;

      // be mindful of the size of the data you are sending here since it will be logged on every request
      paramsSelector?: (params: z.infer<ParamsSchema>) => NetworkAnalyticsDataRecord | undefined;
      responseSelector?: (
        response: z.infer<ResponseSchema>
      ) => NetworkAnalyticsDataRecord | undefined;
    };

export interface QueryRequesterParams<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResultResult,
> {
  // defaults to "cap-srv"
  host: ApiHost;
  // defaults to "post"
  method: QueryMethod;

  path: Path;
  responseSchema: ResponseSchema;
  paramsSchema?: ParamsSchema;

  pathParams?: ExtractParamsFromPath<Path>;
  params?: Record<string, unknown>;
  body?: ReplayableRequestBody;

  transformer?: (response: z.infer<ResponseSchema>) => TransformedResultResult;
  // normalization/parsing to run after zod schema parsing

  signal?: AbortSignal;

  headers?: Record<string, string>;

  // defaults to "basic"
  analytics?: NetworkAnalyticsOptions<ParamsSchema, ResponseSchema>;

  // defaults to false
  assumeUnAuthenticated?: boolean;

  // Set the underlying `fetch` request's `keepalive: true` so the
  // request survives a page unload (reload, close, navigation). Use
  // for beacon-style fire-and-forget sends — e.g. a fatal-error
  // report fired moments before the user clicks Reload. Subject to
  // the Fetch spec's 64 KiB per-document keepalive body ceiling;
  // callers must keep payloads under that.
  keepalive?: boolean;
}

function appendParamsToPath(path: string, params: Record<string, unknown>) {
  if (!params || Object.keys(params).length === 0) {
    return path;
  }

  const [basePath, existingQuery] = path.split("?");
  const searchParams = new URLSearchParams(existingQuery);

  Object.entries(params).forEach(([key, value]) => {
    if (typeof value === "undefined") {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        searchParams.append(key, String(item));
      });
    } else {
      searchParams.append(key, String(value));
    }
  });

  return `${basePath}?${searchParams.toString()}`;
}

export async function queryRequester<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  Result = z.infer<ResponseSchema>,
>(
  config: NetworkingLayerConfig,
  {
    path: originalPath,
    responseSchema,
    params = {},
    pathParams,
    body,
    transformer,
    method = "POST",
    signal,
    headers,
    analytics = { level: "basic" },
    host,
    assumeUnAuthenticated,
    keepalive,
  }: QueryRequesterParams<Path, ParamsSchema, ResponseSchema, Result>
): Promise<Result> {
  let path: string = originalPath;

  const clientTrace = createClientTrace();

  function resolveEventHost() {
    if (typeof host === "string") {
      return host;
    }
    if (host?.type === "custom") {
      return host.host;
    }
    return "cap-srv";
  }
  const eventHost = resolveEventHost();

  // include the proxy to tell if it was proxied or not
  // but we do not need to include the params
  const eventProps = {
    host: eventHost,
    path,
    method,
    request_params: analytics.level === "custom" ? analytics.paramsSelector?.(params) : undefined,
    client_trace: clientTrace,
  };

  const start = performance.now();

  const shouldLogRequest =
    analytics.level === "custom"
      ? (analytics.shouldLogRequest ?? (() => true))
      : () => analytics.level !== "none";

  const trackSuccess = (statusCode: number, responseData: ResponseType) => {
    if (shouldLogRequest("success", params, responseData)) {
      config.trackRequest({
        ...eventProps,
        status: "success",
        status_code: statusCode,
        latency: Math.round(performance.now() - start),

        response_data:
          analytics.level === "custom" ? analytics.responseSelector?.(responseData) : undefined,
      });
    }
  };

  const trackError = (
    statusCode: number,
    error_kind: "known" | "unknown",
    error_message?: string,
    error_code?: string
  ) => {
    if (shouldLogRequest("fail", params)) {
      config.trackRequest({
        ...eventProps,
        status: "fail",
        status_code: statusCode,
        latency: Math.round(performance.now() - start),

        error_kind,
        known_error_message: error_message,
        known_error_code: error_code,
      });
    }
  };

  const trackCancel = () => {
    if (shouldLogRequest("cancel", params)) {
      config.trackRequest({ ...eventProps, status: "cancel" });
    }
  };

  let res: ApiResponse | undefined;
  try {
    path = replacePathParams(path, pathParams);

    if (method === "DELETE" || method === "GET" || typeof body !== "undefined") {
      path = appendParamsToPath(path, params);
    }

    res = await makeApiRequest(config, {
      host: host ?? "cap-srv",
      path,
      body,
      data: params,
      method,
      signal,
      clientTrace,
      headers,
      assumeUnAuthenticated,
      keepalive,
    });

    if (!res.ok) {
      throw new ApiUnavailableError(res.status, res.data);
    }

    const data = responseSchema.parse(res.data);
    trackSuccess(res.status, data);

    return transformer ? transformer(data) : data;
  } catch (e) {
    if (res) {
      const errorInfo = tryToGetErrorInfo(res.data);

      if (res.status === 503) {
        trackError(res.status, "unknown");
        throw new ApiUnavailableError(res.status, res.data);
      }

      if (errorInfo) {
        trackError(res.status, "known", errorInfo.message, errorInfo.code);
        throw new KnownApiError(errorInfo.message, res.status, errorInfo, res.data);
      }
    }

    if (isAbortOrCanceledError(e)) {
      trackCancel();
    } else {
      trackError(res?.status ?? -1, "unknown", `${e}`);
    }

    throw e;
  }
}

type ApiErrorInfo = { message: string; code?: string };

const apiErrorInfoSchemas: readonly z.ZodType<ApiErrorInfo, z.ZodTypeDef, unknown>[] = [
  z.string().transform((message): ApiErrorInfo => ({ message })),
  z
    .object({
      error: z
        .object({
          message: z.string(),
          code: z.union([z.string(), z.number()]).transform(String),
        })
        .passthrough(),
    })
    .transform(({ error }) => error),
  z.object({
    message: z.string(),
  }),
  z
    .object({
      reason: z.string(),
    })
    .transform(({ reason }): ApiErrorInfo => ({ message: reason })),
  z
    .object({
      detail: z.string(),
    })
    .transform(({ detail }): ApiErrorInfo => ({ message: detail })),
];

function tryToGetErrorInfo(data: unknown): ApiErrorInfo | undefined {
  for (const schema of apiErrorInfoSchemas) {
    const parsed = schema.safeParse(data);
    if (parsed.success) {
      return parsed.data;
    }
  }
}
